/**
 * @file data-fixes-tab.tsx
 * @module app/modules/setting/data-fixes-tab
 * @description Settings > Data fixes: customer TIN and phone, run from the web platform once per
 *   environment after a deployment. For each: Check (shows what would change and who would be
 *   blocked; saves nothing, Excel download), Run fix (after a confirm), and an Excel of every change
 *   made. TIN "123-456-789" -> "123456789"; phone "+255 762 952 853" / "0762952853" -> "762952853".
 *   When the fix makes two customers share a TIN / phone, the one with the most orders (then the
 *   latest order) is kept and the others are blocked. Rules: backend utils/customer-data-fix.ts.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.1
 * @version 2.1.1
 */

import { useState } from "react";
import { Alert, Button, Card, Modal, Spinner, Table } from "react-bootstrap";
import * as XLSX from "xlsx";
import APICallService from "../../../api/apiCallService";
import { PURCHASEORDER } from "../../../api/apiEndPoints";
import { Settings } from "../../../utils/constants";
import { error, success } from "../../../Global/toast";

type FixKind = "tin" | "phone";

interface FixRow {
  _id: string;
  name: string;
  businessName: string;
  value: string;
  to?: string;
  orders?: number;
  lastOrderAt?: string | null;
  reason?: string;
}

interface FixPlan {
  summary: {
    total: number;
    blank: number;
    alreadyOk: number;
    toChange: number;
    toBlock: number;
    notFixable: number;
    alreadyBlocked: number;
    alreadyShared: number;
  };
  toChange: FixRow[];
  toBlock: FixRow[];
  notFixable: FixRow[];
  alreadyShared: FixRow[];
}

interface RunResult {
  runId: string;
  done: { changed: number; blocked: number };
  after: FixPlan;
}

interface LogRecord {
  runId: string;
  action: "changed" | "blocked";
  buyer: string;
  name: string;
  businessName: string;
  from: string;
  to: string;
  reason?: string;
  orders?: number;
  lastOrderAt?: string | null;
  by?: { name?: string };
  at: string;
}

const FIXES: Record<FixKind, { title: string; label: string; intro: string }> = {
  tin: {
    title: "Customer TIN",
    label: "TIN",
    intro:
      "Puts saved customer TINs into the 9-digit format: 123-456-789 becomes 123456789. Blank TINs stay blank. TINs that can't be converted (such as 0000 or 1234) are left as they are.",
  },
  phone: {
    title: "Customer phone",
    label: "Phone",
    intro:
      "Puts saved customer phone numbers into digits only, without the country code or a leading 0: +255 762 952 853 and 0762952853 become 762952853. Numbers that are not 9 digits after that are left as they are.",
  },
};

/** Shows spaces / tabs in a saved value, e.g. "122476111·". */
const shown = (v: string) => `"${String(v ?? "").replace(/\s/g, "·")}"`;
const today = () => new Date().toISOString().slice(0, 10);

const sheet = (rows: (string | number)[][]) => {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = rows[0].map((h) => ({ wch: Math.max(14, String(h).length + 4) }));
  return ws;
};

const planRows = (plan: FixPlan, label: string) => {
  const head = ["What happens", "Customer", "Business", `${label} now`, `${label} after`, "Orders", "Last order", "Reason"];
  const line = (what: string, r: FixRow, after: string) => [what, r.name, r.businessName, r.value, after, r.orders ?? "", r.lastOrderAt ?? "", r.reason ?? ""];
  return [
    head,
    ...plan.toChange.map((r) => line("Will be changed", r, r.to ?? "")),
    ...plan.toBlock.map((r) => line("Will be blocked (value left as is)", r, r.value)),
    ...plan.alreadyShared.map((r) => line("Left: already shared before the fix", r, r.value)),
    ...plan.notFixable.map((r) => line(`Left: can't be converted`, r, r.value)),
  ];
};

const RowsTable = ({ title, rows, label, columns }: { title: string; rows: FixRow[]; label: string; columns: ("to" | "orders" | "reason")[] }) =>
  rows.length ? (
    <div className="mt-6">
      <h4 className="fs-16 fw-bolder mb-3">
        {title} ({rows.length.toLocaleString("en-US")})
      </h4>
      <div className="table-responsive" style={{ maxHeight: 320 }}>
        <Table size="sm" className="align-middle table-row-bordered fs-14">
          <thead>
            <tr className="fw-bold text-gray-600">
              <th>Customer</th>
              <th>Business</th>
              <th>{label} now</th>
              {columns.includes("to") ? <th>{label} after</th> : <></>}
              {columns.includes("orders") ? <th>Orders</th> : <></>}
              {columns.includes("orders") ? <th>Last order</th> : <></>}
              {columns.includes("reason") ? <th>Reason</th> : <></>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id}>
                <td>{r.name}</td>
                <td>{r.businessName}</td>
                <td className="font-monospace">{shown(r.value)}</td>
                {columns.includes("to") ? <td className="font-monospace fw-bold">{r.to}</td> : <></>}
                {columns.includes("orders") ? <td>{r.orders ?? 0}</td> : <></>}
                {columns.includes("orders") ? <td>{r.lastOrderAt || "-"}</td> : <></>}
                {columns.includes("reason") ? <td>{r.reason}</td> : <></>}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  ) : (
    <></>
  );

const FixCard = ({ kind }: { kind: FixKind }) => {
  const fix = FIXES[kind];
  const [busy, setBusy] = useState<"" | "check" | "run" | "log">("");
  const [plan, setPlan] = useState<FixPlan | null>(null);
  const [lastRun, setLastRun] = useState<RunResult["done"] | null>(null);
  const [confirm, setConfirm] = useState(false);

  const check = async () => {
    setBusy("check");
    const res: FixPlan | null = await new APICallService(PURCHASEORDER.customerDataFixCheck, { kind }, "", "", "", "", Settings).callAPI();
    setBusy("");
    if (res?.summary) {
      setPlan(res);
      setLastRun(null);
    }
  };

  const run = async () => {
    setConfirm(false);
    setBusy("run");
    const res: RunResult | null = await new APICallService(PURCHASEORDER.customerDataFixRun, { kind }, "", "", "", "", Settings).callAPI();
    setBusy("");
    if (res?.done) {
      setLastRun(res.done);
      setPlan(res.after);
      success(`${fix.title}: ${res.done.changed} changed, ${res.done.blocked} blocked`);
    } else {
      error("The fix did not finish. Check again and retry; changes already made are kept in the log.");
    }
  };

  const downloadCheck = () => {
    if (!plan) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet(planRows(plan, fix.label)), "Check");
    XLSX.writeFile(wb, `${kind}-fix-check-${today()}.xlsx`);
  };

  const downloadChanges = async () => {
    setBusy("log");
    const res: { records: LogRecord[] } | null = await new APICallService(PURCHASEORDER.customerDataFixLog, { kind }, "", "", "", "", Settings).callAPI();
    setBusy("");
    const records = res?.records ?? [];
    if (!records.length) return void error("Nothing has been changed by this fix yet.");
    const rows: (string | number)[][] = [
      ["Date", "Done by", "Action", "Customer", "Business", `${fix.label} before`, `${fix.label} after`, "Orders", "Last order", "Reason", "Customer id", "Run id"],
      ...records.map((r) => [
        new Date(r.at).toLocaleString("en-GB"),
        r.by?.name ?? "",
        r.action === "blocked" ? "Blocked" : "Changed",
        r.name,
        r.businessName,
        r.from,
        r.to,
        r.orders ?? "",
        r.lastOrderAt ?? "",
        r.reason ?? "",
        String(r.buyer),
        r.runId,
      ]),
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet(rows), "Changes");
    XLSX.writeFile(wb, `${kind}-fix-changes-${today()}.xlsx`);
  };

  const s = plan?.summary;
  const pending = s ? s.toChange + s.toBlock : 0;
  return (
    <Card className="border border-gray-300 mb-7" data-testid={`fix-${kind}`}>
      <Card.Body>
        <h3 className="fs-18 fw-bolder mb-2">{fix.title}</h3>
        <p className="fs-14 text-gray-700 mb-2">{fix.intro}</p>
        <p className="fs-14 text-gray-700 mb-4">
          Each {fix.label} must belong to one customer. When the fix makes two customers share one, the customer with the most orders (then
          the latest order) is kept and the others are <b>blocked</b>. Values that customers already shared exactly before the fix are only
          listed. Check first: it saves nothing.
        </p>
        <div className="d-flex flex-wrap gap-3">
          <Button variant="light-primary" disabled={!!busy} onClick={check} data-testid={`${kind}-check`}>
            {busy === "check" ? <Spinner size="sm" animation="border" /> : "Check"}
          </Button>
          <Button variant="primary" disabled={!!busy || !s || pending === 0} onClick={() => setConfirm(true)} data-testid={`${kind}-run`}>
            {busy === "run" ? <Spinner size="sm" animation="border" /> : `Run fix${s && pending ? ` (${s.toChange} to change, ${s.toBlock} to block)` : ""}`}
          </Button>
          <Button variant="light" disabled={!!busy || !plan} onClick={downloadCheck} data-testid={`${kind}-download-check`}>
            Download check (Excel)
          </Button>
          <Button variant="light-success" disabled={!!busy} onClick={downloadChanges} data-testid={`${kind}-download-changes`}>
            {busy === "log" ? <Spinner size="sm" animation="border" /> : "Download changes made (Excel)"}
          </Button>
        </div>

        {s ? (
          <Alert variant={lastRun ? "success" : "secondary"} className="mt-5 mb-0 fs-14" data-testid={`${kind}-summary`}>
            {lastRun ? (
              <div className="fw-bold mb-1">
                Done: {lastRun.changed} changed, {lastRun.blocked} blocked. Download the changes made for the full list.
              </div>
            ) : (
              <></>
            )}
            Customers: {s.total.toLocaleString("en-US")} · blank {s.blank.toLocaleString("en-US")} · already correct{" "}
            {s.alreadyOk.toLocaleString("en-US")} · {lastRun ? "still " : ""}to change {s.toChange} · {lastRun ? "still " : ""}to block{" "}
            {s.toBlock} · left as they are: {s.notFixable} can't be converted, {s.alreadyShared} already shared before the fix
          </Alert>
        ) : (
          <></>
        )}
        {plan ? (
          <>
            <RowsTable title="Will be changed" rows={plan.toChange} label={fix.label} columns={["to", "orders"]} />
            <RowsTable title={`Will be blocked (another customer gets this ${fix.label})`} rows={plan.toBlock} label={fix.label} columns={["orders", "reason"]} />
            <RowsTable title="Left as they are: already shared before the fix (check manually)" rows={plan.alreadyShared} label={fix.label} columns={["orders", "reason"]} />
            <RowsTable title="Left as they are: can't be converted" rows={plan.notFixable} label={fix.label} columns={[]} />
          </>
        ) : (
          <></>
        )}
      </Card.Body>

      <Modal show={confirm} onHide={() => setConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Run the {fix.title.toLowerCase()} fix?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="fs-15">
          In this environment: <b>{s?.toChange}</b> {fix.label}(s) will be changed and <b>{s?.toBlock}</b> customer(s) will be blocked (they can no
          longer log in or order; an admin can unblock them). Every change is logged and can be downloaded in Excel.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={run} data-testid={`${kind}-confirm`}>
            Run fix
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

const DataFixesTab = () => (
  <div data-testid="data-fixes-tab">
    <FixCard kind="tin" />
    <FixCard kind="phone" />
  </div>
);

export default DataFixesTab;
